import joi from 'joi';
import { Record as record } from 'immutable';
import _ from 'lodash';

import config from '../../config';
import trainTime from '../../lib/TrainTime';
import { translate as t } from '../../lib/I18n';

import { SignupTypes } from '../../constants/AppConstants';
import ValidationMixin from '../ValidationMixin';
import validators from '../../validation';
import Photo from '../Photo';
import DateOfBirth from './DateOfBirth';
import Address from './Address';
import FeatureFlagStore from '../../stores/FeatureFlagStore';

const photocardAge = trainTime.duration('P16Y');

const defaults = {
    signup: SignupTypes.WITHOUT_SMARTCARD,
    selfRegistered: false,
    autoDrOptedIn: false,
    requiresOver16: false,
    dateOfBirth: new DateOfBirth(),
    photo: new Photo(),
    isrn: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    townCity: '',
    postcode: '',
    country: 'GBR',
    isOver16: false,
    // TODO: Use referrer instead of isMainRegistration
    isMainRegistration: false
};

class SmartcardRegistration extends record(defaults) {

    getComputedValues() {
        const requiresPhotograph = this.withISRN() ? false : this.requiresPhotograph();
        const requiresISRN = this.withISRN();
        const fileSize = this.photo.selectedImage ? this.photo.selectedImage.size : 0;
        const imageOrientation = this.photo.getImageOrientation();
        const isValidDateOfBirth = this.isValidDateOfBirth();
        const fileFormat = this.photo.selectedImage ? this.photo.selectedImage.type : null;
        const fileWidth = this.photo.imageWidth;
        const fileHeight = this.photo.imageHeight;
        const isEnabledSmartcardRegistration = this.isEnabledSmartcardRegistration();

        return {
            isValidDateOfBirth,
            fileSize,
            requiresPhotograph,
            imageOrientation,
            fileFormat,
            requiresISRN,
            fileWidth,
            fileHeight,
            isEnabledSmartcardRegistration,
        };
    }

    getSchema () {
        if (!this.withSmartcard()) { return {}; }

        const { fileWidth } = this.getComputedValues();

        return {
            addressLine1: validators.addressLine().required(),

            addressLine2: validators.textMaxLength(),

            addressLine3: validators.textMaxLength(),

            townCity: validators.townCity(),

            postcode: validators.postcode(),

            country: joi.any().only(defaults.country),

            isValidDateOfBirth: joi.boolean().only(true).options({
                language: {
                    any: {
                        allowOnly: t('alertmessage.date.invalid')
                    },
                    boolean: { base: t('alertmessage.date.invalid') }
                }
            }),

            isEnabledSmartcardRegistration: joi.boolean().only(true).options({
                language: {
                    any: {
                        allowOnly: t('account.register.smartcard.forbidden-child-smartcard-registering')
                    },
                    boolean: { base: t('account.register.smartcard.forbidden-child-smartcard-registering') }
                }
            }),

            isrn: joi.alternatives().when('requiresISRN', {
                is: true,
                then: validators.isrn(),
                otherwise: joi.any()
            }),

            isOver16: joi.alternatives().when('requiresISRN', {
                is: true,
                then: validators.isOver16(),
                otherwise: joi.alternatives().when('requiresOver16', {
                    is: true,
                    then: validators.isOver16(),
                    otherwise: joi.any()
                })
            }),

            photo: joi.alternatives().when('requiresPhotograph', {
                is: true,
                then: joi.object().keys({ photo: validators.requiredString() }),
                otherwise: joi.any()
            }),

            fileHeight: fileWidth && joi.alternatives().when('requiresPhotograph', {
                is: true,
                then: joi.number().min(fileWidth + 1),
                otherwise: joi.any()
            }),

            fileFormat: !_.isUndefined(window.FileReader) && config.get('smartcardPhotoMimeTypes') && config.get('smartcardPhotoMimeTypes').length ?
                joi.string().only(null, config.get('smartcardPhotoMimeTypes')) :
                joi.any()
        };
    }

    getAddress() {
        return new Address({
            line1: this.addressLine1,
            line2: this.addressLine2,
            line3: this.addressLine3,
            town: this.townCity,
            postCode: this.postcode,
            country: this.country,
        });
    }

    getDateOfBirth () {
        return _.result(this.dateOfBirth, 'getDateOfBirth');
    }

    requiresPhotograph () {
        const dateOfBirth = this.getDateOfBirth();
        const photocardDate = trainTime().subtract(photocardAge);

        return this.signup && !this.isOver16 && dateOfBirth && dateOfBirth.isAfter(photocardDate);
    }

    isValidDateOfBirth() {
        return this.isMainRegistration && this.isOver16 ? true : _.result(this.dateOfBirth, 'isComplete');
    }

    withSmartcard () {
        return this.signup === SignupTypes.WITH_SMARTCARD;
    }

    withISRN() {
        return this.selfRegistered;
    }

    withoutSmartcard () {
        return this.signup === SignupTypes.WITHOUT_SMARTCARD;
    }

    withTransfer () {
        return this.signup === SignupTypes.WITH_TRANSFER;
    }

    isDateOfBirthOver16YearsOld () {
        const dateOfBirth = this.getDateOfBirth();
        if (!dateOfBirth) {
            return false;
        }
        const sixteenYearsAgo = new Date();
        sixteenYearsAgo.setFullYear(sixteenYearsAgo.getFullYear() - 16);
        return dateOfBirth.isBefore(sixteenYearsAgo);
     }

    isEnabledSmartcardRegistration () {
        return (
            this.isOver16 ||
            FeatureFlagStore.get('enableChildSmartcardRegistration') ||
            !this.isValidDateOfBirth() ||
            this.isDateOfBirthOver16YearsOld()
        );
    }
}

Object.assign(SmartcardRegistration.prototype, ValidationMixin);

export default SmartcardRegistration;



// WEBPACK FOOTER //
// ./src/records/customer/SmartcardRegistration.js